import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { listPublishedJobs } from "@/lib/careers.functions";
import { JobCard } from "@/components/careers/JobCard";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/careers/")({
  head: () => ({
    meta: [
      { title: "Careers | Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content: "Join our team and be part of our mission to make healthy food accessible and meaningful. Explore job opportunities at Poonam Kapur Healthy Kitchen.",
      },
      { property: "og:title", content: "Careers | Poonam Kapur Healthy Kitchen" },
      { property: "og:description", content: "Join our team and help us redefine healthy eating in Mumbai." },
      { property: "og:url", content: "https://poonamkapur.com/careers" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/careers" }],
  }),
  component: CareersListingPage,
});

function CareersListingPage() {
  const fetchJobs = useServerFn(listPublishedJobs);

  const { data: jobs, isLoading, error } = useQuery({
    queryKey: ["careers", "list"],
    queryFn: () => fetchJobs(),
  });

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Careers"
        title="Work with us."
        intro="Join our team and be part of our mission to make healthy food accessible and meaningful."
      />

      <Section className="pt-0">
        {isLoading ? (
          <div className="flex min-h-[300px] flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm text-muted-foreground">Loading opportunities...</p>
          </div>
        ) : error ? (
          <div className="rounded-3xl border border-dashed border-destructive/30 bg-destructive/5 p-12 text-center">
            <h3 className="font-display text-xl">Something went wrong</h3>
            <p className="mt-2 text-muted-foreground">We couldn't load the job listings right now. Please try refreshing the page.</p>
          </div>
        ) : !jobs || jobs.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-12 text-center">
            <h3 className="font-display text-xl">No open positions right now</h3>
            <p className="mt-2 text-muted-foreground">We aren't currently hiring for any roles, but check back soon!</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </Section>
    </SiteLayout>
  );
}
